import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AlbumDensity, AlbumDraftChoices, AlbumDraftOptions, AlbumFormat, AlbumStyles, TripPhoto } from '../../types';
import { albumApi } from '../../api/album.api';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';
import AlbumFormatPicker from './AlbumFormatPicker';
import AlbumThemePicker from './AlbumThemePicker';
import AlbumPhotoPicker from './AlbumPhotoPicker';

interface Props {
  open: boolean;
  tripId: string;
  /** `restart` rewrites every page of the album there is; its own photos stay (ADR-0051). */
  mode: 'create' | 'restart';
  formats: AlbumFormat[];
  styles: AlbumStyles;
  photos: TripPhoto[];
  initial: Required<Pick<AlbumDraftChoices, 'format' | 'theme' | 'density'>>;
  onClose: () => void;
  onSubmit: (choices: AlbumDraftChoices) => Promise<void>;
}

const STEPS = ['format', 'theme', 'density', 'cover'] as const;
const DENSITIES: AlbumDensity[] = ['FEW', 'NORMAL', 'ALL'];

/**
 * ADR-0050: the album is shaped before its draft — format, theme, how many
 * photos a day and the cover. Every step has a default, and «Create» is there
 * from the first one: skipping them all is the old one-tap album.
 */
export default function AlbumWizard({ open, tripId, mode, formats, styles, photos, initial, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [choices, setChoices] = useState(initial);
  const [coverPhotoId, setCoverPhotoId] = useState<string | null>(null);
  const [options, setOptions] = useState<AlbumDraftOptions | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setChoices(initial);
    setCoverPhotoId(null);
    albumApi.draftOptions(tripId).then(setOptions).catch(() => setOptions(null));
    // `initial` is read only on opening: a re-render of the parent must not reset the steps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tripId]);

  const cover = coverPhotoId ?? options?.coverPhotoId ?? null;
  const name = STEPS[step];
  const last = step === STEPS.length - 1;

  async function submit() {
    setSubmitting(true);
    try {
      await onSubmit({ ...choices, coverPhotoId: cover });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t(`album.wizard.title_${mode}`)} size="lg">
      <div className="space-y-5">
        <ol className="flex gap-1.5" aria-label={t('album.wizard.steps')}>
          {STEPS.map((s, i) => (
            <li key={s} className="flex-1">
              <button
                onClick={() => setStep(i)}
                aria-current={i === step ? 'step' : undefined}
                className={`w-full text-left text-[11px] font-semibold pt-1.5 border-t-2 cursor-pointer ${
                  i <= step ? 'border-teal-500 text-slate-800 dark:text-slate-100' : 'border-slate-200 dark:border-slate-700 text-slate-400'
                }`}
              >
                {t(`album.wizard.${s}.step`)}
              </button>
            </li>
          ))}
        </ol>

        <section className="space-y-3 min-h-[16rem]">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">{t(`album.wizard.${name}.title`)}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t(`album.wizard.${name}.body`)}</p>
          </div>

          {name === 'format' && (
            <AlbumFormatPicker formats={formats} value={choices.format} disabled={false} onChange={format => setChoices(c => ({ ...c, format }))} />
          )}
          {name === 'theme' && (
            <AlbumThemePicker styles={styles} value={choices.theme} onChange={theme => setChoices(c => ({ ...c, theme }))} />
          )}
          {name === 'density' && (
            <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" aria-label={t('album.wizard.density.title')}>
              {DENSITIES.map(d => {
                const pages = options?.pages[d];
                const tooLong = !!options && pages !== undefined && pages > options.maxPages;
                return (
                  <button
                    key={d}
                    role="radio"
                    aria-checked={choices.density === d}
                    onClick={() => setChoices(c => ({ ...c, density: d }))}
                    className={`rounded-xl p-3 text-left cursor-pointer ${choices.density === d ? 'ring-2 ring-teal-500' : 'ring-1 ring-slate-200 dark:ring-slate-700 hover:ring-teal-300'}`}
                  >
                    <span className="block text-sm font-semibold text-slate-900 dark:text-slate-100">{t(`album.wizard.density.${d}`)}</span>
                    <span className="block mt-0.5 text-xs text-slate-500 dark:text-slate-400">{t(`album.wizard.density.${d}_hint`)}</span>
                    <span className={`block mt-2 text-xs font-medium ${tooLong ? 'text-amber-600' : 'text-slate-600 dark:text-slate-300'}`}>
                      {pages === undefined ? '…' : tooLong
                        ? t('album.wizard.density.tooLong', { max: options!.maxPages })
                        : t('album.wizard.density.pages', { count: pages })}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {name === 'cover' && (
            <AlbumPhotoPicker photos={photos} usedIds={new Set()} selectedId={cover} onPick={setCoverPhotoId} />
          )}
        </section>

        {mode === 'restart' && <p className="text-xs text-amber-700 dark:text-amber-400">{t('album.wizard.restartWarning')}</p>}

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <button onClick={() => setStep(s => s - 1)} disabled={step === 0 || submitting} className="btn-ghost-sm disabled:invisible">
            {t('common.back')}
          </button>
          <div className="flex gap-2">
            {!last && (
              <button onClick={() => setStep(s => s + 1)} disabled={submitting} className="btn-secondary">
                {t('album.wizard.next')}
              </button>
            )}
            <button onClick={() => void submit()} disabled={submitting} className="btn-primary gap-2">
              {submitting && <Spinner size="sm" />}
              {t(`album.wizard.submit_${mode}`)}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
